import cx from 'classnames';
import { BROADCAST_EVENTS } from 'constants/dom-element.const';
import {
  KEYCODE_TO_TAKE_PHOTO,
  TIME_TO_CAMERA_FLASH,
} from 'constants/photo.const';
import { useRobotArmHandler } from 'hooks/useRobotArmHandler';
import { includes } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useAppSelector } from 'store/store-hooks';
import PhotoThumbnailList from './PhotoThumbnailList';
import './shooting.css';
import { ShootingContext, useShootingContext } from './ShootingContext';
import { ShootingControls } from './ShootingControls';
import ShootingLiveView from './ShootingLiveView';
import { useShootingHandlers } from './useShootingHandlers';

function Shooting() {
  const { handleMoveRobotArm } = useRobotArmHandler();
  const { isVerticalScreen } = useAppSelector((s) => s.booth);

  const {
    countDownSec,
    isFlashCamera,
    setIsFlashCamera,
    isCountDownMode,
    isVerticalFrame,
    isDisplayOnLiveview,
  } = useShootingContext();

  const {
    handleTakePhoto,
    handleListenTakePhoto,
    handleListenTakePhotoFailed,
    delayTimeCountDownSeconds,
    timeLimitSeconds,
    handleStartPhoto,
    handleContinueOrConfirmReTakePhoto,
  } = useShootingHandlers();

  const [triggerShowTheme, setTriggerLiveViewRef] = useState(false);

  // unfreeze sau số giây chụp ảnh
  useEffect(() => {
    if (isFlashCamera) {
      setTimeout(() => {
        setIsFlashCamera(false);
      }, TIME_TO_CAMERA_FLASH);
    }
  }, [isFlashCamera, setIsFlashCamera]);

  // Realtime remote to take a picture
  useEffect(() => {
    document.addEventListener(
      BROADCAST_EVENTS.TAKE_PHOTO,
      handleListenTakePhoto,
    );
    return () => {
      document.removeEventListener(
        BROADCAST_EVENTS.TAKE_PHOTO,
        handleListenTakePhoto,
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCountDownMode]);

  // Event take photo failed from canon
  useEffect(() => {
    document.addEventListener(
      BROADCAST_EVENTS.TAKE_PHOTO_FAILED,
      handleListenTakePhotoFailed,
    );
    return () => {
      document.removeEventListener(
        BROADCAST_EVENTS.TAKE_PHOTO_FAILED,
        handleListenTakePhotoFailed,
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCountDownMode]);

  // Realtime remote to take a picture by logitech present
  useEffect(() => {
    if (!isCountDownMode) {
      const listener = (e: KeyboardEvent) => {
        if (includes(KEYCODE_TO_TAKE_PHOTO, e.keyCode)) {
          handleTakePhoto();
        }
      };
      document.addEventListener('keyup', listener);
      return () => {
        document.removeEventListener('keyup', listener);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCountDownMode]);

  React.useEffect(() => {
    handleMoveRobotArm(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // fix lỗi theme show trên liveview không hiện ngay từ khi vào Màn chụp ảnh
  useEffect(() => {
    setTimeout(() => setTriggerLiveViewRef(true), 50);
  }, []);

  return (
    <>
      <div
        className={cx('flex flex-col items-center shooting-page', {
          'shooting-page-overhead': true,
          'shooting-page-with-vertical-frame': isVerticalFrame,
          'shooting-page-with-theme-on-live-view': isDisplayOnLiveview,
        })}
      >
        <ShootingControls
          delayTimeCountDownSeconds={delayTimeCountDownSeconds}
          countDownSec={countDownSec}
          timeLimitSeconds={timeLimitSeconds}
          handleStartPhoto={handleStartPhoto}
          handleContinueOrConfirmReTakePhoto={
            handleContinueOrConfirmReTakePhoto
          }
        />

        {isVerticalScreen && (
          <PhotoThumbnailList triggerShowTheme={triggerShowTheme} />
        )}
        <ShootingLiveView triggerShowTheme={triggerShowTheme} />
        {!isVerticalScreen && (
          <PhotoThumbnailList triggerShowTheme={triggerShowTheme} />
        )}
      </div>
    </>
  );
}

const ShootingWithContext = () => (
  <ShootingContext>
    <Shooting />
  </ShootingContext>
);

export default ShootingWithContext;
